import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Button, Form, FormControl, ControlLabel, Icon } from "rsuite";
import styled, { css } from "styled-components";
import ModalSubmitRowApprove from "./ModalSubmitRowApprove";
import { SCREEN_MEDIA } from "const";
import { isObject, buildClearedValue, compareObjects } from "utils/helpers";
import { focusPickerSearch } from "utils/inputs";

const { md } = SCREEN_MEDIA;


export default (
    {
        columns,
        formModel,
        onSubmit,
        onDataUpdate,
        mobile,
        addButtonWidth,
        formStyle,
        formDefaultValue = {},
        extraOnChange = null,
        extraOnSubmit = null,
        disabled = false,
        extraDisabledCreate = false,

        onChangeServiceName
    }
) => {

    const formRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const [modalApproveData, setModalApproveData] = useState(false);
    const [showModalApprove, setShowModalApprove] = useState(false);
    const [modalApproveLoading, setModalApproveLoading] = useState(null);

    const clearedDefaultValue = useMemo(() => {
        if (formDefaultValue && Object.keys(formDefaultValue).length) return formDefaultValue;
        const clearedValue = buildClearedValue(columns, formDefaultValue);
        return clearedValue;
    }, []);

    const [formStateValue, setFormStateValue] = useState(clearedDefaultValue);

    const getDisabled = useCallback((formControlProps) => {
        const currentFormValue = Object.keys(formStateValue).length ? formStateValue : formDefaultValue;

        return formControlProps?.disabledFunc && typeof formControlProps?.disabledFunc === "function" ? formControlProps?.disabledFunc(currentFormValue) : false ||
            formControlProps?.disabled ||
            formControlProps?.disabledOnCreate;
    }, [formStateValue]);


    const handleSubmit = () => {
        if (!formRef.current.check())
            return;

        const formValue = formRef.current.getFormValue();

        columns.forEach(column => {
            if (!formValue.hasOwnProperty(column?.dataKey) && column.hasOwnProperty("defaultValue") && column.replaceWithDefault) {
                formValue[column.dataKey] = column.defaultValue
            }
        })

        setLoading(true);
        onSubmit(formValue)
            .then(res => {
                setLoading(false);
                if (res) {
                    onDataUpdate();
                    const clearedValue = buildClearedValue(columns, formDefaultValue);

                    if (extraOnSubmit) {
                        extraOnSubmit();
                    }

                    setFormStateValue((prev) => {
                        return {
                            ...prev,
                            ...clearedValue
                        };
                    });
                }
            });
    };

    const onApprovedSubmit = async () => {
        setModalApproveLoading(true);
        await onSubmit(modalApproveData);
        onDataUpdate();
        setModalApproveLoading(false);
        setShowModalApprove(false);
    };

    const fieldsCount = columns.filter(item => item.accepter).length;

    useEffect(() => {
        const columnListFiltered = columns.filter((value) => {
            return !!value.fixTextareaValue;
        });
        const textareaKeyList = columnListFiltered.map((value) => {
            return value.dataKey;
        });

        Object.keys(formStateValue).forEach((key) => {
            if (textareaKeyList.includes(key) && !compareObjects(formStateValue, clearedDefaultValue)) {
                formRef.current.check();
            }
        });
    }, [formStateValue]);

    return (
        <>
            <StyledForm
                ref={formRef}
                model={formModel}
                onSubmit={handleSubmit}
                formValue={formStateValue}
                onChange={(formValue) => {
                    const columnListFiltered = columns.filter(v => !!v.fixTextareaValue);
                    const textareaKeyList = columnListFiltered.map(v => v.dataKey);

                    const textareaFormValues = textareaKeyList.reduce((result, key) => {
                        if (formValue[key]) {
                            if (
                                Object.prototype.hasOwnProperty.call(formValue, key) &&
                                isObject(formValue[key]) &&
                                formValue[key].hasOwnProperty("target")
                            ) {
                                result[key] = formValue[key].target.value;
                            } else {
                                result[key] = formValue[key];
                            }
                        }
                        return result;
                    }, {});

                    const formFormattedValue = {
                        ...formValue,
                        ...textareaFormValues
                    };

                    if (extraOnChange) {
                        const safe = JSON.parse(JSON.stringify(formValue));
                        extraOnChange(safe);
                    }

                    setFormStateValue(formFormattedValue);
                }}
                formStyle={formStyle}
                mobile={mobile}
            >

                {columns.map(({
                    dataKey, fieldName, accepter, name = "", value, defaultValue, model,
                    flexGrow = null, minWidth = null, maxWidth = null, width = null, createWidth = null,
                    label, showLabelOnCreate,
                    customData = false, data = null, createData = [], labelStyle = {}, extraCreateProps = {},
                    ...formControlProps
                }) => {

                    const currentData = typeof data === "function"
                        ? data(formStateValue)
                        : data || [];


                    const isDisabled = getDisabled({ ...formControlProps, ...extraCreateProps });
                    const fieldWidth = createWidth ?? width;

                    return accepter &&
                        <FlexBox
                            grow={flexGrow}
                            minWidth={minWidth}
                            maxWidth={maxWidth}
                            width={fieldWidth}
                        >
                            {showLabelOnCreate && !labelStyle.atTheEnd &&
                                !labelStyle.checkboxLabel && <FormLabel {...labelStyle}>{label}</FormLabel>}
                            <StyledFieldWrapper
                                cols={fieldsCount}
                                grow={flexGrow}
                                minWidth={minWidth}
                                maxWidth={maxWidth}
                                width={fieldWidth}
                            >
                                <StyledField
                                    accepter={accepter}
                                    mobile={mobile}
                                    name={fieldName || dataKey}
                                    className="tableFilters_field"
                                    errorPlacement="topEnd"
                                    placeholder="Description"
                                    type="text"
                                    onChange={(value) => {
                                        if (dataKey === "antf_name") {
                                            onChangeServiceName(value)
                                        }
                                    }}
                                    {...formControlProps}
                                    {...extraCreateProps}
                                    // defaultValue={defaultValue}
                                    {...(customData ? {
                                        data: createData
                                    } : {
                                        data: currentData
                                    })}
                                    onOpen={() => {
                                        if (formControlProps.onOpen) formControlProps.onOpen();
                                        if (formControlProps.searchable === false) return;
                                        focusPickerSearch();
                                    }}
                                    showLabelOnCreate={showLabelOnCreate}
                                    checkboxLabel={labelStyle.checkboxLabel}
                                    compactCheckboxLabel={labelStyle.compactCheckboxLabel}
                                    labelWidth={labelStyle.labelWidth || 100}
                                    disabled={isDisabled}
                                >
                                    {showLabelOnCreate && labelStyle.checkboxLabel ? <FormLabel {...labelStyle}>{label}</FormLabel> : null}
                                </StyledField>
                            </StyledFieldWrapper>
                            {showLabelOnCreate && labelStyle.atTheEnd &&
                                !labelStyle.checkboxLabel && <FormLabel {...labelStyle}>{label}</FormLabel>}
                        </FlexBox>
                })}

                <StyledSubmitWrapper
                    mobile={mobile}
                    addButtonWidth={addButtonWidth}
                >
                    <Button
                        className="tableFilters_submit"
                        color="green"
                        type="submit"
                        loading={loading || extraDisabledCreate}
                        disabled={disabled || extraDisabledCreate}
                    >
                        {mobile
                            ? <b>Create</b>
                            : <Icon icon="plus" />
                        }
                    </Button>
                </StyledSubmitWrapper>
            </StyledForm>

            <ModalSubmitRowApprove
                show={showModalApprove}
                onSubmit={onApprovedSubmit}
                onClose={() => setShowModalApprove(false)}
                disabled={modalApproveLoading}
            />
        </>
    )
};

const FlexBox = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;

    flex-grow: ${props => props.grow || '0'};
    flex-shrink: 1;
    ${props => {
        const width = props.minWidth || props.width;
        return width && css`
            width: ${width}px;
            flex-basis: ${width}px;
        `
    }}

    ${props => props.maxWidth && css`
        max-width: ${props.maxWidth}px;
    `}
`;

const FormLabel = styled(ControlLabel)`
    &&&& {
        float: none;
        ${props => !props.noMargin && `
            margin: 0 0 10px 0;
        `};

        ${props => props.noMargin && css`
            margin: 0px !important;
        `};

        padding: 0;
        text-align: left;
        word-wrap: none;

        @media (min-width: ${md.min}px) {
            ${props => !props.noMargin && `
                margin: 0 10px 0 0;
            `};

            ${props => props.noMargin && css`
                margin: 0px !important;
            `};

            max-width: 130px;
            width: auto;
        }
    }
`;


const StyledField = styled(FormControl).attrs(props => {
    return {
        className: "tableCreate_field",
        errorPlacement: "topEnd",
        placement: "autoVerticalStart",
        type: "text",
        cleanable: props.cleanable !== undefined ? props.cleanable : true,
        searchable: props.searchable !== undefined ? props.searchable : true,
    }
})`
    ${props => props.showLabelOnCreate && props.checkboxLabel && css`
        .rs-checkbox-wrapper {
            width: ${props => props.labelWidth}px;
        }
    `}

    ${props => props.showLabelOnCreate && props.checkboxLabel && props.compactCheckboxLabel && css`
        .rs-checkbox-checker {
            align-items: center;
            display: flex;
            line-height: 14px;
            min-height: 28px;
            white-space: nowrap;
        }
    `}

    && .rs-picker-toggle, &&.rs-input, &&.rs-input-number, &&.rs-picker-date .rs-picker-toggle {
        border-color: gray !important;
        border-width: 2px !important;
        border-style: solid !important;
    }

    && .rs-picker-toggle.active,
    &&.rs-input:focus,
    &&.rs-input-number:focus-within {
        border-width: 1px !important;
    }

    && .rs-picker-toggle:hover, &&.rs-input:hover, &&.rs-input-number:hover, &&.rs-picker-date:hover {
        border-color: lightgray !important;
    }

    &&.rs-picker-input {
        border: none !important;
    }

    &&.rs-picker-input, &&.rs-picker-input .rs-picker-toggle {
        height: 38px !important;
    }
`;

const StyledFieldWrapper = styled.div`
    flex-grow: ${props => props.grow || '0'};
    flex-shrink: 1;
    padding: 5px 5px;
    ${props => {
        const width = props.minWidth || props.width;
        return width && css`
            width: ${width}px;
            flex-basis: ${width}px;
        `
    }}
    ${props => props.maxWidth && css`
        max-width: ${props.maxWidth}px;
    `}
`;

const StyledSubmitWrapper = styled.div`
    flex-shrink: 0;
    padding: 5px 10px;
    width: ${props => props.mobile ? '100%' : (props.addButtonWidth ? props.addButtonWidth : 174)};
`;

const StyledForm = styled(Form)`
    display: flex;

    ${props => props.formStyle && ({ ...props.formStyle })}
    ${props => props.mobile && css`
        flex-wrap: wrap;
    `}
`;
